<template>
  <div class="bg-white font-poppins">
    <h3 class="sr-only">Items in your cart</h3>
    <div role="list" class="border rounded-lg border-solid border-black text-black px-4 sm:px-6 py-2">
      <div v-for="product in cartItems" :key="product.id" role="listitem" class="flex justify-between py-4">
        <div class="min-w-0 flex-1">
          <h4 class="text-md font-poppins-bold text-black my-0 py-0 flex items-center">
            {{ itemLabel(product) }}
            <div v-if="product.code === 'NDC'" class="tooltip ml-1">
              <component :is=InformationCircleIcon class="h-5 w-5 text-brand-button-text" aria-hidden="true"/>
              <span class="tooltiptext">
                <p class="tooltipmsg">The New Development Charge or NDC is a charge that is passed on from the network wholesale provider to residents via the Retail Service Provider. This fee is applied to all new developments as a one-time fee for the first user to activate an internet service.</p>
              </span>
            </div>
          </h4>
          <p v-if="isPlanHardwareOrVoice(product) && product.description" class="text-sm mt-0 mb-0 py-0">
            {{ product.description }}
          </p>
          <span v-if="product.type === 'internet'" class="inline-block text-xs font-bold mt-2 py-0.5 uppercase text-white bg-black px-4 rounded-full tracking-widest">
            Unlimited data
          </span>
        </div>

        <div class="ml-6 flex flex-col items-end text-right">
          <p class="my-0 py-0 font-poppins-bold text-md" :class="[isStruckThrough(product) ? 'line-through text-gray-300' : 'text-gray-900']">{{ utilityService.formatCurrency(product.price) }}</p>
          <p v-if="isPromoValid && product.discounted" class="my-0 py-0 font-poppins-bold text-md text-gray-900">{{ utilityService.formatCurrency(product.discountedPrice ?? 0) }}</p>
          <p v-if="product.type === 'internet' || product.type === 'voice'" class="text-sm my-0 py-0">Monthly</p>
        </div>
      </div>
    </div>
    <dl class="">
      <div class="flex flex-col items-start my-6 justify-between py-6 px-4 sm:px-6 border rounded-lg border-solid border-black text-black">
        <h2 class="text-lg font-poppins-bold my-0 py-0">Promotions</h2>
        <h3 class="sr-only">Enter promotion code here</h3>
        <div class="flex flex-col w-full">
          <label for="promoCode" class="block text-md font-medium text-black pb-1">Do you have a promotion code?</label>
          <div class="flex flex-row pt-4 justify-between">
            <input type="text" name="promoCode" id="promoCode" autocomplete="off" v-model="promoCode" class="uppercase block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none max-w-md text-lg font-poppins font-bold px-6" />
            <button @click="promoCodeClickHandler" type="button" class="cursor-pointer items-center ml-2 px-4 py-2 border border-black border-solid shadow-sm text-sm md:text-lg font-poppins rounded-full text-black bg-white hover:bg-black hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent">Apply Code</button>
          </div>
          <p v-if="isPromoValid" class="mt-1 font-normal text-sm text-green-500">Promo code {{ validPromotion?.code }} applied!</p>
          <p v-if="!isPromoValid && promoCode && promoCode.length > 0" class="mt-1 font-normal text-sm text-red-500">Promo code invalid!</p>
        </div>
      </div>
      <div v-if="isPromoValid && promoCode" class="flex items-left justify-start py-4 px-4 sm:px-6 border rounded-lg border-solid border-black text-black flex-col">
        <h2 class="text-lg font-poppins-bold my-0 py-0">Promotion includes:</h2>
        <div v-if="delayedNdc" class="flex items-center justify-between border-t border-gray-200 py-1 px-4 sm:px-6">
          <dt class="text-sm font-extralight">
            &#8226; The new development fee of $300 will be applied in month 13
          </dt>
        </div>
        <div v-if="yearOff" class="flex items-center justify-between border-t border-gray-200 py-1 px-4 sm:px-6">
          <dt class="text-sm font-extralight">
            &#8226; First Year Free! - Standard billing will commence in month 13
          </dt>
        </div>
        <div v-if="monthOff" class="flex items-center justify-between border-t border-gray-200 py-1 px-4 sm:px-6">
          <dt class="text-sm font-extralight">
            &#8226; First Month Free!
          </dt>
        </div>
        <div v-if="preInstalledRouter" class="flex items-center justify-between border-t border-gray-200 py-1 px-4 sm:px-6">
          <dt class="text-sm font-extralight">
            &#8226; Pre-Installed router
          </dt>
        </div>
      </div>
      <div class="flex flex-row items-start my-6 justify-between py-6 px-4 sm:px-6 border rounded-lg border-solid border-black text-black">
        <div class="text-md font-poppins-bold">
          First Invoice Total
          <dt class="text-md font-poppins pt-6 max-w-md">
          The total minimum cost of service over duration of the contract selected less any discounts is <span class="font-poppins-bold">{{ utilityService.formatCurrency(orderService.calculateMinimumCost(products, isPromoValid, validPromotion)) }}</span>
          </dt>
        </div>
        <div class="text-md font-poppins-bold">
          {{ utilityService.formatCurrency(orderService.calculateFirstInvoiceTotal(products, isPromoValid, validPromotion)) }}
        </div>
      </div>
    </dl>
  </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, defineProps, ref, watch } from 'vue';
import { injectStrict } from '@/plugins/vueServiceContainerPlugin';
import { Product } from '@/adapters/catalogAdapterInterface';
import { OrderServiceInterface } from '@/services/orderServiceInterface';
import { UtilityServiceInterface } from '@/services/utilityServiceInterface';
import { InformationCircleIcon } from '@heroicons/vue/outline';
import { isActionApplicableToPlan } from '@/utils/promotionActions';
import { Action, Promotion } from '@uniti-internet/promotions-js-client/dist/utilities/types';

const orderService = injectStrict<OrderServiceInterface>('OrderServiceInterface');
const utilityService = injectStrict<UtilityServiceInterface>('UtilityServiceInterface');

const props = defineProps<{
  products: Product[],
  isPromoValid: boolean,
  promotionDiscounts: Array<Action>,
  validPromotion?: Promotion,
  suggestedPromotion?: Promotion,
  promoCode?: string
}>();

const emit = defineEmits<{(e: 'onEnterPromoCode', promoCodeInput?: string): void}>();

// Order the cart the way customers read it: plan, router, VoIP, then one-off fees.
const cartItemOrder = (product: Product): number => {
  if (product.type === 'internet') return 0;
  if (product.type === 'hardware') return 1;
  if (product.type === 'voice') return 2;
  if (product.code === 'ACTIVATION-FEE') return 3;
  if (product.code === 'NDC') return 4;
  return -1;
};

const cartItems = computed(() => {
  return props.products
    .filter(product => cartItemOrder(product) >= 0)
    .sort((a, b) => cartItemOrder(a) - cartItemOrder(b));
});

const isPlanHardwareOrVoice = (product: Product): boolean => {
  return product.type === 'internet' || product.type === 'hardware' || product.type === 'voice';
};

const itemLabel = (product: Product): string => {
  if (product.type === 'internet') return `Plan: ${product.name}`;
  if (product.type === 'hardware') return `Router: ${product.name}`;
  if (product.type === 'voice') return `VoIP: ${product.name}`;
  if (product.code === 'ACTIVATION-FEE') return 'Activation Fee';
  return 'New Development Fee';
};

// Plans, routers and VoIP strike through when discounted; fees strike through when the discount is displayed.
const isStruckThrough = (product: Product): boolean => {
  if (!props.isPromoValid) return false;
  return isPlanHardwareOrVoice(product) ? Boolean(product.discounted) : Boolean(product.displayDiscount);
};

const delayedNdc = computed(() => {
  return props.promotionDiscounts.find(discount => discount.actionCatalogObject.code === 'DELAYED-NDC');
});

const yearOff = computed(() => {
  return props.promotionDiscounts.find(discount => discount.actionCatalogObject.code === 'YEAR-OFF' && isActionApplicableToPlan(discount, props.products));
});

const monthOff = computed(() => {
  let plan = '';
  if (props.products.length > 0 && props.products.filter(pr => pr.type === 'internet').length > 0) {
    plan = props.products.filter(pr => pr.type === 'internet')[0].id;
  }
  return props.promotionDiscounts.find(discount => discount.actionCatalogObject.code === 'ONE-OFF' && (discount.applicableTo.length <= 0 || (discount.applicableTo.length > 0 && discount.applicableTo.includes(plan))));
});

const preInstalledRouter = computed(() => {
  return props.promotionDiscounts.find(discount => discount.actionCatalogObject.code === 'FREE-ROUTER-INSTALLED');
});

const promoCode = ref(props.promoCode ?? props.suggestedPromotion?.code);

// Keep the input in sync when the promo code is set by the parent (e.g. a suggested promotion).
watch(() => props.promoCode, (code) => {
  if (code !== undefined) {
    promoCode.value = code;
  }
});

function promoCodeClickHandler () {
  let promoCodeUppercase;
  if (promoCode.value) {
    promoCodeUppercase = promoCode.value.toUpperCase();
  }
  emit('onEnterPromoCode', promoCodeUppercase);
}
</script>
